import { Component, input, signal } from '@angular/core';

export type UiAvatarSize = 'sm' | 'md' | 'lg';
export type UiAvatarVariant = 'soft' | 'brand' | 'topbar';

export function initialsFromName(name: string): string {
  const trimmed = (name || '?').trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

@Component({
  selector: 'ui-avatar',
  standalone: true,
  template: `
    @if (src() && !broken()) {
      <img
        class="ui-avatar"
        [class.ui-avatar--sm]="size() === 'sm'"
        [class.ui-avatar--md]="size() === 'md'"
        [class.ui-avatar--lg]="size() === 'lg'"
        [class.ui-avatar--soft]="variant() === 'soft'"
        [class.ui-avatar--brand]="variant() === 'brand'"
        [class.ui-avatar--topbar]="variant() === 'topbar'"
        [src]="src()!"
        [alt]="alt()"
        (error)="onError()"
      />
    } @else {
      <span
        class="ui-avatar ui-avatar--initials"
        [class.ui-avatar--sm]="size() === 'sm'"
        [class.ui-avatar--md]="size() === 'md'"
        [class.ui-avatar--lg]="size() === 'lg'"
        [class.ui-avatar--soft]="variant() === 'soft'"
        [class.ui-avatar--brand]="variant() === 'brand'"
        [class.ui-avatar--topbar]="variant() === 'topbar'"
        aria-hidden="true"
      >{{ displayInitials() }}</span>
    }
  `,
  styles: [`
    .ui-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      border-radius: 999px;
      object-fit: cover;
      box-sizing: border-box;
    }

    .ui-avatar--sm {
      width: 2rem;
      height: 2rem;
      font-size: 0.75rem;
    }

    .ui-avatar--md {
      width: 2.75rem;
      height: 2.75rem;
      font-size: 0.875rem;
    }

    .ui-avatar--lg {
      width: 3rem;
      height: 3rem;
      font-size: 1rem;
    }

    .ui-avatar--initials {
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    .ui-avatar--soft.ui-avatar--initials {
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 14%, #fff);
      color: var(--brand-primary, #1677ff);
      border: 1px solid color-mix(in srgb, var(--brand-primary, #1677ff) 18%, transparent);
    }

    .ui-avatar--brand,
    .ui-avatar--brand.ui-avatar--initials {
      background: var(--brand-primary, #1677ff);
      border: 2px solid var(--brand-primary, #1677ff);
      box-shadow: 0 2px 10px color-mix(in srgb, var(--brand-primary, #1677ff) 32%, transparent);
    }

    .ui-avatar--brand.ui-avatar--initials {
      color: var(--on-brand-primary, #fff);
    }

    .ui-avatar--topbar.ui-avatar--initials {
      background: var(--topbar-surface-hover, var(--on-brand-primary-hover));
      color: var(--topbar-fg, var(--on-brand-primary));
      border: 1px solid var(--topbar-border, var(--on-brand-primary-border));
    }
  `],
})
export class UiAvatarComponent {
  src = input<string | null>(null);
  name = input('');
  alt = input('');
  size = input<UiAvatarSize>('md');
  variant = input<UiAvatarVariant>('soft');

  readonly broken = signal(false);

  displayInitials(): string {
    return initialsFromName(this.name() || this.alt() || '?');
  }

  onError(): void {
    this.broken.set(true);
  }
}
